import { AlertColor } from '@mui/material';
import { PropsWithChildren, createContext, useContext, useRef, useState, type JSX } from 'react';

export interface IMessage {
  content?: string | JSX.Element;
  severity?: AlertColor;
  show: boolean;
}

export interface IMessageContext {
  message: IMessage;
  showMessage: (content?: string | JSX.Element, severity?: AlertColor, timeout?: number) => void;
  hideMessage: () => void;
}

const MessageContext = createContext<IMessageContext>({
  message: { show: false },
  showMessage: () => {},
  hideMessage: () => {},
});

export const MessageProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [message, setMessage] = useState<IMessage>({ show: false });
  // A ref (not useState) so clearing/setting the pending auto-hide timer is synchronous and
  // always reflects the true latest timer. showMessage/hideMessage are recreated every render,
  // and with a state-backed timer, two calls in close succession (no re-render in between) would
  // each close over the same stale value - the second call couldn't see the timer the first one
  // just scheduled, orphaning it. That orphaned timer fires later on its own schedule and hides
  // whatever message happens to be showing at that point, with no relation to user action.
  const scheduledRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const hideMessage = () => {
    setMessage((previous) => {
      return { ...previous, show: false };
    });
    if (scheduledRef.current) {
      clearTimeout(scheduledRef.current);
      scheduledRef.current = undefined;
    }
  };

  const showMessage = (
    content?: string | JSX.Element,
    severity: AlertColor = 'success',
    timeout = 5000
  ) => {
    if (scheduledRef.current) {
      clearTimeout(scheduledRef.current);
      scheduledRef.current = undefined;
    }
    setMessage({ content, severity, show: true });
    if (timeout > 0) {
      scheduledRef.current = setTimeout(hideMessage, timeout);
    }
  };

  return <MessageContext value={{ message, showMessage, hideMessage }}>{children}</MessageContext>;
};

export function useMessage() {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error('useMessage must be used within MessageProvider');
  }
  return context;
}
